import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Modal from '../components/Modal';
import PrintPortal from '../components/PrintPortal';
import { inr, num, dt, day, apiError } from '../utils/format';
import { useAuth } from '../context/AuthContext';
import { packsOf } from '../utils/packs';
import { useLocationFilter } from '../context/LocationContext';
import { beep, errorBeep, isMuted, setMuted } from '../utils/beep';
import { printBill, normBill } from '../components/BillDesign';
import { OpenRegisterCard, CloseRegisterModal, RegisterBar } from '../components/RegisterModals';

const Receipt = ({ sale, taxSummary, billTemplate, onClose }) => {
  // Built once, rendered twice: on screen in the modal, and into the print portal.
  // Printing straight from the modal put the bill in a fixed-position subtree, which
  // browsers paint on the first sheet only — every page after it came out blank.
  const doc = (
    <div className="receipt-doc">
      <h3 style={{ textAlign: 'center', margin: 0 }}>{sale.location?.shopName || normBill(billTemplate).shopName}</h3>
      <p className="muted" style={{ textAlign: 'center', margin: '.2rem 0 1rem' }}>{sale.location?.name} · {sale.saleNumber}</p>
      {sale.customer && <p className="muted" style={{ margin: '0 0 .4rem', fontSize: '.8rem' }}>{sale.customer.name}{sale.customer.phone ? ` · ${sale.customer.phone}` : ''}</p>}
      {sale.shipToAddress && (
        <p style={{ margin: '0 0 .6rem', fontSize: '.8rem' }}>
          <strong>Deliver to{sale.shipToName ? ` (${sale.shipToName})` : ''}:</strong> {sale.shipToAddress}
          {sale.shipToPhone ? ` · ${sale.shipToPhone}` : ''}
        </p>
      )}
      <table className="grid" style={{ fontSize: '.82rem' }}>
        <thead><tr><th>Product</th><th>Qty</th><th className="right">Rate</th><th className="right">Amount</th></tr></thead>
        <tbody>
          {sale.items.map((it) => (
            <tr key={it.id}>
              <td>{it.name} {it.packLabel}{Number(it.discount) > 0 ? <div className="muted" style={{ fontSize: '.72rem' }}>less {inr(it.discount)}</div> : null}</td>
              <td>{it.quantity}</td><td className="right">{inr(it.unitPrice)}</td><td className="right">{inr(it.lineTotal)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt">
        <div className="bill-row"><span>Taxable</span><span>{inr(sale.taxableAmount)}</span></div>
        {taxSummary?.map((g) => (
          <div className="bill-row muted" key={g.rate}><span>GST {g.rate}% (CGST {inr(g.cgst)} + SGST {inr(g.sgst)})</span><span>{inr(g.cgst + g.sgst)}</span></div>
        ))}
        {Number(sale.discount) > 0 && <div className="bill-row"><span>Discount</span><span>−{inr(sale.discount)}</span></div>}
        {Number(sale.roundOff) !== 0 && <div className="bill-row"><span>Round off</span><span>{inr(sale.roundOff)}</span></div>}
        <div className="bill-row total"><span>Total</span><span>{inr(sale.netTotal)}</span></div>
        {(sale.payments || []).map((p) => <div className="bill-row muted" key={p.id}><span>{p.mode.toUpperCase()}</span><span>{inr(p.amount)}</span></div>)}
      </div>
      <p className="muted" style={{ textAlign: 'center', marginTop: '1rem', fontSize: '.8rem' }}>Thank you! Visit again.</p>
    </div>
  );

  return (
    <Modal title={`Bill ${sale.saleNumber}`} onClose={onClose} footer={<><button className="btn btn-outline" onClick={onClose}>New sale</button><button className="btn btn-primary" onClick={() => printBill(billTemplate, sale, taxSummary)}>Print</button></>}>
      {doc}
      <PrintPortal>{doc}</PrintPortal>
    </Modal>
  );
};

// A discount as the cashier enters it: an amount, or a percentage of whatever it
// applies to. Kept as typed so switching ₹ ⇄ % does not silently rewrite what was
// agreed with the customer, and resolved to rupees only when the bill is priced.
const discountAmount = (value, mode, base) => {
  const n = Number(value) || 0;
  if (n <= 0 || base <= 0) return 0;
  const amt = mode === 'pct' ? (base * Math.min(n, 100)) / 100 : n;
  return Math.min(Math.round(amt * 100) / 100, base);
};

// Naming a held bill is what makes it findable when three are waiting. Optional,
// because at a rush the cashier will just take the next customer.
const HoldModal = ({ total, items, onHold, onClose }) => {
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const hold = async () => { setBusy(true); await onHold(label); setBusy(false); };
  return (
    <Modal title="Hold this bill" onClose={onClose}
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-gold" onClick={hold} disabled={busy}>{busy ? 'Holding…' : `Hold · ${inr(total)}`}</button></>}>
      <p className="muted small" style={{ marginTop: 0 }}>
        {items} item{items === 1 ? '' : 's'} set aside for {inr(total)}. The till clears for the next customer; nothing is billed and no stock moves.
      </p>
      <div className="field"><label>Whose bill is it?</label>
        <input value={label} onChange={(e) => setLabel(e.target.value)} autoFocus
          placeholder="Name, phone, or how you will recognise them"
          onKeyDown={(e) => { if (e.key === 'Enter' && !busy) hold(); }} />
      </div>
    </Modal>
  );
};

// A held cart, however the database chose to hand it back.
const heldLines = (h) => {
  const raw = h && h.items;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') { try { const v = JSON.parse(raw); return Array.isArray(v) ? v : []; } catch { return []; } }
  return [];
};

const HeldModal = ({ held, onResume, onDiscard, onClose }) => (
  <Modal wide title={`Held bills (${held.length})`} onClose={onClose}
    footer={<button className="btn btn-outline" onClick={onClose}>Close</button>}>
    {held.length === 0 ? <p className="muted">Nothing is on hold at this branch.</p> : (
      <div className="table-wrap">
        <table className="grid">
          <thead><tr><th>Held for</th><th>Held</th><th>By</th><th className="right">Products</th><th className="right">Total</th><th></th></tr></thead>
          <tbody>
            {held.map((h) => (
              <tr key={h.id}>
                <td><strong>{h.label || h.code}</strong>{h.label ? <div className="muted small">{h.code}</div> : null}</td>
                <td className="muted small">{dt(h.createdAt)}</td>
                <td className="muted small">{h.user?.name || '—'}</td>
                <td className="right">{heldLines(h).length}</td>
                <td className="right"><strong>{inr(h.total)}</strong></td>
                <td className="right" style={{ whiteSpace: 'nowrap' }}>
                  <button className="btn btn-primary btn-sm" onClick={() => onResume(h)}>Resume</button>{' '}
                  <button className="btn btn-danger btn-sm" title="Discard this held bill" onClick={() => onDiscard(h)}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </Modal>
);

// Numbers are kept as country code + local digits and stored E.164 (+919876543210).
// Matching is on the last ten digits, so a customer saved as a bare 10-digit
// number before country codes existed still comes back when +91 is prefixed.
const DEFAULT_CC = '+91';
const localDigits = (v) => String(v || '').replace(/\D/g, '');
const normCc = (v) => {
  const d = localDigits(v);
  return d ? `+${d}` : '';
};
const e164 = (cc, phone) => `${normCc(cc) || DEFAULT_CC}${localDigits(phone)}`;
const last10 = (v) => localDigits(v).slice(-10);
// India is ten digits; elsewhere varies, so only insist on something plausible.
const phoneOk = (cc, phone) => {
  const d = localDigits(phone);
  return (normCc(cc) === '+91' || !cc) ? d.length === 10 : d.length >= 6;
};

// Phone first, because that is what identifies a returning customer. Ten digits
// in, the name fills itself from whoever is already on file — a regular is one
// field, and a new customer is two. `known` is reported up so callers can say
// what that person already owes.
const useCustomerLookup = (phone, name, setName, onKnown) => {
  useEffect(() => {
    const digits = localDigits(phone);
    if (digits.length < 6) { onKnown(null); return undefined; }
    let live = true;
    api.get(`/customers?q=${encodeURIComponent(digits)}`).then(({ data }) => {
      if (!live) return;
      const hit = (data.customers || []).find((c) => last10(c.phone) === last10(digits));
      onKnown(hit || null);
      if (hit && !String(name || '').trim()) setName(hit.name);
    }).catch(() => {});
    return () => { live = false; };
  }, [phone]);   // eslint-disable-line react-hooks/exhaustive-deps
};

// Every bill this customer has taken, newest first — opened from the line the
// till shows when a known phone is typed.
const CustomerBillsModal = ({ customer, onClose }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});   // billId → showing its products
  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));
  useEffect(() => {
    api.get(`/customers/${customer.id}/bills?limit=100`).then(({ data: d }) => setData(d))
      .catch((e) => { toast.error(apiError(e)); onClose(); });
  }, [customer.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal wide title={`${customer.name}${customer.phone ? ` · ${customer.phone}` : ''}`} onClose={onClose}
      footer={<button className="btn btn-outline" onClick={onClose}>Close</button>}>
      {!data ? <div className="spinner" /> : (
        <>
          <div className="flex gap" style={{ flexWrap: 'wrap', marginBottom: '.6rem' }}>
            <span className="chip-static">{data.summary.count} bill{data.summary.count === 1 ? '' : 's'}</span>
            <span className="chip-static">{inr(data.summary.spent)} spent</span>
            {data.summary.outstanding > 0.005 && <span className="badge badge-red">{inr(data.summary.outstanding)} outstanding</span>}
          </div>
          <div className="table-wrap">
            <table className="grid">
              <thead><tr><th>Bill</th><th>When</th><th className="right">Products</th><th>Paid by</th><th className="right">Amount</th><th className="right">Due</th><th>Status</th></tr></thead>
              <tbody>
                {data.bills.map((b) => {
                  const items = b.items || [];
                  const showing = !!open[b.id];
                  return (
                    <React.Fragment key={b.id}>
                      <tr style={{ cursor: items.length ? 'pointer' : 'default' }}
                        onClick={() => items.length && toggle(b.id)} title={items.length ? 'Show what was bought' : undefined}>
                        <td className="mono">{items.length ? <span className="muted">{showing ? '▾' : '▸'}</span> : null} {b.saleNumber}</td>
                        <td className="muted small">{dt(b.createdAt)}</td>
                        <td className="right muted small">{items.length || '—'}</td>
                        <td className="muted small">{(b.modes || []).map((m) => m.toUpperCase()).join(' + ') || '—'}</td>
                        <td className="right"><strong>{inr(b.netTotal)}</strong></td>
                        <td className="right">{b.due > 0.005 ? <strong className="neg">{inr(b.due)}</strong> : '—'}</td>
                        <td className="muted small">{b.status.replace('_', ' ')}</td>
                      </tr>
                      {showing && items.map((i, idx) => (
                        <tr key={`${b.id}-${idx}`} className="bill-item-row">
                          <td></td>
                          <td colSpan={3} className="small">{i.name} <span className="muted">{i.packLabel}</span></td>
                          <td className="right small">{num(i.quantity)} × {inr(i.lineTotal / (i.quantity || 1))}</td>
                          <td className="right small">{inr(i.lineTotal)}</td>
                          <td></td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
                {!data.bills.length && <tr><td colSpan={7} className="empty">No bills yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
};

// A delivery address, entered once and reused. Kept on the customer so the next
// festival order does not start with someone dictating their street again.
const AddressModal = ({ customerId, onSaved, onClose }) => {
  const [form, setForm] = useState({ label: '', line1: '', line2: '', landmark: '', city: '', state: '', pincode: '', phone: '' });
  const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await api.post(`/customers/${customerId}/addresses`, form);
      toast.success('Address saved');
      onSaved(data.address); onClose();
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Modal title="Add a delivery address" onClose={onClose}
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy || !form.line1.trim()}>{busy ? 'Saving…' : 'Save address'}</button></>}>
      <div className="row">
        <div className="field"><label>Label</label><input value={form.label} onChange={(e) => set('label', e.target.value)} placeholder="Home, Shop…" autoFocus /></div>
        <div className="field" style={{ flex: 2 }}><label>Address line 1 *</label><input value={form.line1} onChange={(e) => set('line1', e.target.value)} placeholder="Flat / house, street" /></div>
      </div>
      <div className="row">
        <div className="field" style={{ flex: 2 }}><label>Address line 2</label><input value={form.line2} onChange={(e) => set('line2', e.target.value)} placeholder="Area, colony" /></div>
        <div className="field"><label>Landmark</label><input value={form.landmark} onChange={(e) => set('landmark', e.target.value)} /></div>
      </div>
      <div className="row">
        <div className="field"><label>City</label><input value={form.city} onChange={(e) => set('city', e.target.value)} /></div>
        <div className="field"><label>State</label><input value={form.state} onChange={(e) => set('state', e.target.value)} /></div>
        <div className="field"><label>PIN</label><input value={form.pincode} onChange={(e) => set('pincode', e.target.value)} inputMode="numeric" /></div>
      </div>
      <div className="field"><label>Phone at this address</label>
        <input value={form.phone} onChange={(e) => set('phone', e.target.value)} inputMode="tel" placeholder="If someone else receives it" />
      </div>
      {error && <p className="form-error">{error}</p>}
    </Modal>
  );
};

// Counter sale or delivery. Only offered once we know who the customer is —
// an address has to belong to somebody.
const ShipTo = ({ customerId, addresses, shipToId, setShipToId, onAdded }) => {
  const [adding, setAdding] = useState(false);
  if (!customerId) return <p className="muted small" style={{ margin: '.2rem 0 0' }}>Enter the customer to add a delivery address.</p>;
  return (
    <div className="field field-inline" style={{ marginTop: '.4rem' }}>
      <label>Deliver to</label>
      <div className="flex gap">
        {/* With nothing saved there is nothing to choose between — the button is
            the whole control. A dropdown offering one non-answer is noise. */}
        {addresses.length > 0 && (
          <select value={shipToId} onChange={(e) => setShipToId(e.target.value)} style={{ flex: 1 }}>
            <option value="">No delivery</option>
            {addresses.map((a) => (
              <option key={a.id} value={a.id}>{a.label ? `${a.label} — ` : ''}{a.oneLine}</option>
            ))}
          </select>
        )}
        <button className="btn btn-outline btn-sm" style={addresses.length ? undefined : { flex: 1 }} onClick={() => setAdding(true)}>
          ＋ Address
        </button>
      </div>
      {adding && <AddressModal customerId={customerId} onSaved={onAdded} onClose={() => setAdding(false)} />}
    </div>
  );
};

const CustomerFields = ({ customer, setCustomer, known, setKnown, autoFocus, required }) => {
  useCustomerLookup(customer.phone, customer.name, (n) => setCustomer((c) => ({ ...c, name: n })), setKnown);
  const digits = localDigits(customer.phone);
  const shortPhone = digits.length > 0 && !phoneOk(customer.cc, customer.phone);
  const [summary, setSummary] = useState(null);
  const [showBills, setShowBills] = useState(false);

  useEffect(() => {
    if (!known) { setSummary(null); return undefined; }
    let live = true;
    api.get(`/customers/${known.id}/bills?limit=1`)
      .then(({ data }) => { if (live) setSummary(data.summary); }).catch(() => {});
    return () => { live = false; };
  }, [known && known.id]);   // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <div className="field field-inline"><label>Phone{required ? ' *' : ''}</label>
        <div className="phone-box">
          <input className="phone-cc" value={customer.cc ?? DEFAULT_CC} inputMode="tel" title="Country code"
            onChange={(e) => setCustomer((c) => ({ ...c, cc: e.target.value }))} />
          <input value={customer.phone} inputMode="tel" autoFocus={autoFocus} placeholder="10-digit mobile"
            style={shortPhone ? { color: 'var(--danger)' } : undefined}
            onChange={(e) => setCustomer((c) => ({ ...c, phone: e.target.value }))} />
        </div>
      </div>
      <div className="field field-inline"><label>Name{required ? ' *' : ''}</label>
        <input value={customer.name} placeholder="Customer name"
          onChange={(e) => setCustomer((c) => ({ ...c, name: e.target.value }))} />
      </div>
      {known && (
        <p className="muted small" style={{ margin: '.25rem 0 0' }}>
          {summary && summary.last
            ? <>Last bill <strong>{inr(summary.last.amount)}</strong> on {day(summary.last.at)}</>
            : <>Known customer</>}
          {summary && summary.count > 1 ? ` · ${summary.count} bills` : ''}
          {Number(known.creditBalance) > 0 && <span className="neg"> · owes {inr(known.creditBalance)}</span>}
          {' '}
          <button type="button" className="link-strong" style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
            onClick={() => setShowBills(true)}>See all bills</button>
        </p>
      )}
      {showBills && known && <CustomerBillsModal customer={known} onClose={() => setShowBills(false)} />}
    </>
  );
};

// Credit needs a name and a number: a bill nobody can chase is a gift.
const PayLaterModal = ({ total, customer: initial, onConfirm, onClose }) => {
  const [customer, setCustomer] = useState({ cc: initial?.cc || DEFAULT_CC, phone: initial?.phone || '', name: initial?.name || '' });
  const [known, setKnown] = useState(null);
  const [busy, setBusy] = useState(false);

  const ok = customer.name.trim() && phoneOk(customer.cc, customer.phone);
  const confirm = async () => {
    setBusy(true);
    await onConfirm({ name: customer.name.trim(), phone: e164(customer.cc, customer.phone) });
    setBusy(false);
  };

  return (
    <Modal title="Pay later" onClose={onClose}
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={confirm} disabled={busy || !ok}>{busy ? 'Saving…' : `Bill on credit · ${inr(total)}`}</button></>}>
      <p className="muted small" style={{ marginTop: 0 }}>
        The goods go out and the bill is raised now; <strong>{inr(total)}</strong> stays outstanding against the customer until it is settled.
      </p>
      <CustomerFields customer={customer} setCustomer={setCustomer} known={known} setKnown={setKnown} autoFocus />
      {!ok && <p className="muted small">A phone and a name are needed — a credit bill has to be chaseable.</p>}
    </Modal>
  );
};

const Billing = () => {
  const { user } = useAuth();
  const { locationId: locFilter, locations: allLocations } = useLocationFilter();
  // Selling location comes from the top-bar filter (owner) or the cashier's own branch.
  const locationId = locFilter || (user?.locationId ? String(user.locationId) : '');
  const locName = (allLocations.find((l) => String(l.id) === String(locationId)) || {}).name;
  const [products, setProducts] = useState([]);
  const [stock, setStock] = useState({});          // variantId -> qty at location
  const [cart, setCart] = useState([]);            // {variantId, productId, name, packLabel, unitPrice, gstRate, qty}
  const [query, setQuery] = useState('');   // one box: scan barcode / type code or name
  const [discount, setDiscount] = useState('');
  const [discountMode, setDiscountMode] = useState('amt');   // amt | pct
  const [payments, setPayments] = useState([]);    // {mode, amount}
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const scanRef = useRef(null);
  // The cashier's register session at this branch. No session (or a closed day)
  // means no billing — the drawer count would not reconcile otherwise.
  const [session, setSession] = useState(null);
  const [dayClosed, setDayClosed] = useState(false);
  const [lastClose, setLastClose] = useState(null);   // how this branch's drawer was last left
  const [muted, setMutedState] = useState(isMuted);
  const [billTemplate, setBillTemplate] = useState(() => normBill(null));
  const [held, setHeld] = useState([]);          // carts parked at this branch
  const [holding, setHolding] = useState(false);
  const [showHeld, setShowHeld] = useState(false);
  const [payLater, setPayLater] = useState(false);
  const [customer, setCustomer] = useState({ cc: DEFAULT_CC, phone: '', name: '' });
  const [knownCustomer, setKnownCustomer] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [shipToId, setShipToId] = useState('');
  // Every bill is billed to somebody: both fields are required before charging.
  const customerOk = customer.name.trim().length > 0 && phoneOk(customer.cc, customer.phone);
  const customerPayload = () => ({ name: customer.name.trim(), phone: e164(customer.cc, customer.phone) });
  const [sessionChecked, setSessionChecked] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => { api.get('/products').then(({ data }) => setProducts(data.products || [])); }, []);
  // How a printed bill should look — set in Settings, read once here.
  useEffect(() => {
    api.get('/settings').then(({ data }) => setBillTemplate(normBill(data.settings && data.settings.billTemplate))).catch(() => {});
  }, []);

  const loadSession = useCallback(() => {
    if (!locationId) { setSession(null); setSessionChecked(true); return; }
    api.get(`/pos/session/current?locationId=${locationId}`)
      .then(({ data }) => { setSession(data.session); setDayClosed(!!data.dayClosed); setLastClose(data.lastClose || null); })
      .catch(() => { setSession(null); setDayClosed(false); })
      .finally(() => setSessionChecked(true));
  }, [locationId]);
  // Reloads after every sale too (receipt changes), so the drawer figure is live.
  useEffect(loadSession, [loadSession, receipt]);
  // The topbar can close the register from any screen; it says so on this channel.
  useEffect(() => {
    const onChange = () => loadSession();
    window.addEventListener('pos-session', onChange);
    return () => window.removeEventListener('pos-session', onChange);
  }, [loadSession]);
  const announce = () => window.dispatchEvent(new Event('pos-session'));

  // A known customer brings their addresses with them; the default is preselected
  // because that is where nearly every delivery goes.
  useEffect(() => {
    if (!knownCustomer) { setAddresses([]); setShipToId(''); return; }
    api.get(`/customers/${knownCustomer.id}/addresses`).then(({ data }) => {
      setAddresses(data.addresses || []);
      const def = (data.addresses || []).find((a) => a.isDefault);
      setShipToId(def ? String(def.id) : '');
    }).catch(() => setAddresses([]));
  }, [knownCustomer && knownCustomer.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  const loadHeld = useCallback(() => {
    if (!locationId) { setHeld([]); return; }
    api.get(`/pos/held?locationId=${locationId}`).then(({ data }) => setHeld(data.held || [])).catch(() => setHeld([]));
  }, [locationId]);
  useEffect(loadHeld, [loadHeld]);

  // Park the cart as it stands — quantities and line discounts included — and
  // clear the till for the next customer.
  const holdBill = async (label) => {
    try {
      await api.post('/pos/held', {
        locationId: Number(locationId), label,
        items: cart.map((c) => ({
          key: c.key, variantId: c.variantId, productId: c.productId, loose: !!c.loose, weighed: !!c.weighed, unit: c.unit,
          name: c.name, packLabel: c.packLabel,
          unitPrice: c.unitPrice, gstRate: c.gstRate, qty: qtyOf(c), disc: c.disc, discMode: c.discMode,
        })),
        discount: billDisc, total: net,
      });
      toast.success(label ? `Held for ${label}` : 'Bill held');
      setCart([]); setDiscount(''); setDiscountMode('amt'); setPayments([]);
      setHolding(false); loadHeld(); scanRef.current?.focus();
    } catch (e) { toast.error(apiError(e)); }
  };

  // Resuming replays the cart and releases the hold. The till must be empty
  // first: silently merging two customers' baskets is how the wrong person pays.
  const resumeHeld = async (h) => {
    if (cart.length) return toast.error('Finish or hold the current bill first');
    try {
      await api.delete(`/pos/held/${h.id}`);
      // Replay against today's catalogue: the till shows what the API will charge,
      // so a price changed since the bill was parked cannot leave the two arguing
      // at the payment step. A pack withdrawn in the meantime keeps its held price.
      const lines = heldLines(h);
      if (!lines.length) { toast.error('That held bill has no lines left to resume'); loadHeld(); return; }
      setCart(lines.map((i) => {
        const key = i.key || (i.variantId ? `v${i.variantId}` : `p${i.productId}`);
        return { ...i, ...(variantIndex[key] || {}), key, qty: i.qty, disc: i.disc || '', discMode: i.discMode || 'amt' };
      }));
      setDiscount(Number(h.discount) > 0 ? String(h.discount) : ''); setDiscountMode('amt');
      setShowHeld(false); loadHeld(); scanRef.current?.focus();
      toast.success(`Resumed ${h.label || h.code}`);
    } catch (e) { toast.error(apiError(e)); loadHeld(); }
  };

  const discardHeld = async (h) => {
    if (!window.confirm(`Discard the bill held for ${h.label || h.code} (${inr(h.total)})?`)) return;
    try { await api.delete(`/pos/held/${h.id}`); toast.success('Held bill discarded'); loadHeld(); }
    catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => {
    if (!locationId) { setStock({}); return; }
    api.get(`/inventory/levels?locationId=${locationId}&itemType=product`).then(({ data }) => {
      const map = {};
      for (const l of data.levels) {
        const k = l.variantId ? `v${l.variantId}` : `p${l.itemId}`;
        map[k] = (map[k] || 0) + Number(l.quantity);
      }
      setStock(map);
    }).catch(() => {});
  }, [locationId, receipt]);

  // A product is sold by the kilo or by the piece. Only the kilo is divisible,
  // and only off the bulk stock — a variant is a pack, and a pack is counted
  // whatever the product behind it is measured in. (GMS/LTR/ML appear in older
  // rows; they are measured too.)
  const WEIGHED = /^(kgs?|g|gms?|grams?|l|lts?|ltrs?|lit(re|er)s?|ml)$/i;
  const isWeighed = (u) => WEIGHED.test(String(u || '').trim());
  const SELLABLE_LOOSE = /^(pcs?|pieces?)$/i;

  // A pack labelled 1KG on a product measured in KGS is not a sealed size — it
  // is where the per-kilo price is kept, and half a kilo is sold off that line.
  // (In this catalogue only one product in 248 carries a per-kg price of its
  // own; the 1KG pack IS the price.) A 250G or 500G jar is a sealed size and
  // stays countable.
  const ONE_UNIT_LABEL = /^\s*1\s*(kgs?|l|lts?|ltrs?|lit(re|er)s?)\s*$/i;
  const isWholeUnitPack = (prod, v) => isWeighed(prod.unit)
    && (Number(v.weightGrams) === 1000 || ONE_UNIT_LABEL.test(v.weightLabel || ''));

  const variantIndex = useMemo(() => {
    const idx = {};
    for (const p of products) {
      for (const v of packsOf(p)) {   // skips retired packs and Loose products
        idx[`v${v.id}`] = {
          key: `v${v.id}`, variantId: v.id, productId: p.id, loose: false,
          weighed: isWholeUnitPack(p, v), unit: p.unit,
          name: p.name, code: p.code || '', packLabel: v.weightLabel,
          unitPrice: Number(v.sellingPrice), gstRate: Number(p.gstRate), barcode: v.barcode, sku: v.sku,
        };
      }
      // Anything measured rather than counted can also be sold loose, at the
      // product's own price per kg (or per whatever its unit is).
      const unit = p.unit || 'unit';
      const hasPacks = packsOf(p).length > 0;
      if (!hasPacks && (isWeighed(unit) || SELLABLE_LOOSE.test(unit)) && Number(p.sellingPrice) > 0) {
        idx[`p${p.id}`] = {
          key: `p${p.id}`, variantId: 0, productId: p.id, loose: true, weighed: isWeighed(unit), unit,
          name: p.name, code: p.code || '', packLabel: isWeighed(unit) ? `Loose (${unit})` : `Each (${unit})`,
          unitPrice: Number(p.sellingPrice), gstRate: Number(p.gstRate), barcode: p.barcode, sku: '',
        };
      }
    }
    return idx;
  }, [products]);

  const addVariant = (key) => {
    const v = variantIndex[key];
    if (!v) return;
    beep();
    setCart((prev) => {
      const i = prev.findIndex((c) => c.key === v.key);
      // A repeat scan bumps the quantity where the line already sits — moving it
      // would shuffle the list under the cashier's eyes on every beep.
      // Loose goods step by 0.5 of their unit; packs by one piece.
      const step = v.weighed ? 0.5 : 1;
      if (i >= 0) return prev.map((c, idx) => (idx === i ? { ...c, qty: Math.round(((Number(c.qty) || 0) + step) * 1000) / 1000 } : c));
      // A new line goes on top: the thing just scanned is the thing to check.
      return [{ ...v, qty: step, disc: '', discMode: 'amt' }, ...prev];
    });
  };

  const exactHit = (code) => {
    const eq = (a) => String(a || '').toLowerCase() === code.toLowerCase();
    const byPack = Object.entries(variantIndex).find(([, v]) => eq(v.barcode) || eq(v.sku));
    if (byPack) return byPack[0];
    const byCode = Object.entries(variantIndex).filter(([, v]) => eq(v.code));
    return byCode.length === 1 ? byCode[0][0] : null;
  };

  // A scanner types the whole barcode fast — auto-add the moment the value matches
  // a pack exactly, so the cashier never has to press Enter.
  const onQueryChange = (val) => {
    setQuery(val);
    const code = val.trim();
    if (!code) return;
    const hit = exactHit(code);
    if (hit) { addVariant(hit); setQuery(''); }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const code = query.trim();
    if (!code) return;
    // 1) exact barcode / SKU / product code → add straight away (scanner behaviour)
    const local = exactHit(code);
    if (local) { addVariant(local); setQuery(''); return; }
    // 2) a name/code/barcode partial match → add the top result
    if (matches.length) { addVariant(matches[0][0]); setQuery(''); return; }
    // 3) ask the API by barcode
    try {
      const { data } = await api.get(`/products/by-barcode/${encodeURIComponent(code)}`);
      if (data.variant) { addVariant(data.variant.id); setQuery(''); }
      else { errorBeep(); toast('No matching product', { icon: 'ℹ️' }); }
    } catch { errorBeep(); toast.error('No product for that code'); }
  };

  // Held as typed — "0", "0.", "0.5" are all valid states on the way to half a
  // kilo — and read through qtyOf wherever a number is needed.
  const setQty = (key, qty) => setCart((prev) => prev.map((c) => (c.key === key ? { ...c, qty } : c)));

  // Tidied when the box is left, not while it is being used: a weight to three
  // decimals, a pack to whole jars, and never zero.
  const commitQty = (key) => setCart((prev) => prev.map((c) => {
    if (c.key !== key) return c;
    const n = Number(c.qty) || 0;
    return { ...c, qty: weighs(c) ? Math.max(0.001, Math.round(n * 1000) / 1000) : Math.max(1, Math.round(n)) };
  }));
  const setLineDisc = (key, patch) => setCart((prev) => prev.map((c) => (c.key === key ? { ...c, ...patch } : c)));
  const removeLine = (key) => setCart((prev) => prev.filter((c) => c.key !== key));

  // Lines the books cannot cover. Selling them is allowed; going quiet is not.
  const qtyOf = (c) => Number(c.qty) || 0;
  const weighs = (c) => !!c.weighed;

  const short = cart.filter((c) => stock[c.key] != null && qtyOf(c) > stock[c.key])
    .map((c) => ({ ...c, qty: qtyOf(c), have: stock[c.key] }));

  const lineGross = (c) => c.unitPrice * qtyOf(c);
  const lineDisc = (c) => discountAmount(c.disc, c.discMode, lineGross(c));
  const lineNet = (c) => lineGross(c) - lineDisc(c);

  const gross = cart.reduce((s, c) => s + lineGross(c), 0);
  const lineDiscTotal = cart.reduce((s, c) => s + lineDisc(c), 0);
  // The bill discount comes off what is left after the line discounts — taking it
  // off the full total would give the same rupee away twice.
  const afterLines = gross - lineDiscTotal;
  const billDisc = discountAmount(discount, discountMode, afterLines);
  const discTotal = lineDiscTotal + billDisc;
  const net = Math.round(afterLines - billDisc);
  const paid = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
  const due = net - paid;

  // At most two payment methods (e.g. Cash + UPI/Card), and each mode only once.
  const addPay = (mode, amount) => setPayments((p) => (p.length >= 2 || p.some((x) => x.mode === mode)) ? p : [...p, { mode, amount: amount != null ? amount : Math.max(0, due) }]);
  const rmPay = (i) => setPayments((p) => p.filter((_, idx) => idx !== i));
  // Editing one payment auto-balances the LAST line to the remaining, so the
  // payments always add up to the bill total when splitting across modes.
  const setPayAmount = (i, val) => setPayments((prev) => {
    const next = prev.map((p, idx) => (idx === i ? { ...p, amount: Number(val) || 0 } : p));
    const last = next.length - 1;
    if (last > 0 && last !== i) {
      const others = next.reduce((s, p, idx) => (idx === last ? s : s + (Number(p.amount) || 0)), 0);
      next[last] = { ...next[last], amount: Math.max(0, Math.round(net - others)) };
    }
    return next;
  });

  // Both routes to a finished bill share everything except how it was paid.
  const submitSale = async (paymentLines, customer) => {
    setBusy(true);
    try {
      const { data } = await api.post('/sales', {
        locationId: Number(locationId), channel: 'pos', discount: billDisc,
        items: cart.map((c) => (c.loose
          ? { productId: c.productId, quantity: qtyOf(c), discount: lineDisc(c) }
          : { variantId: c.variantId, quantity: qtyOf(c), discount: lineDisc(c) })),
        payments: paymentLines, customer,
        shipToAddressId: shipToId ? Number(shipToId) : undefined,
      });
      setReceipt({ sale: data.sale, taxSummary: data.taxSummary });
      for (const w of (data.warnings || [])) {
        toast(`${w.name} ${w.packLabel}: sold ${num(w.sold)} with ${num(w.have)} on the books — stock is now ${num(w.have - w.sold)}. Check the shelf.`,
          { icon: '⚠️', duration: 7000 });
      }
      setCart([]); setDiscount(''); setDiscountMode('amt'); setPayments([]); setPayLater(false);
      setCustomer({ cc: DEFAULT_CC, phone: '', name: '' }); setKnownCustomer(null);
      setAddresses([]); setShipToId('');
      return true;
    } catch (e) { toast.error(apiError(e)); return false; }
    finally { setBusy(false); scanRef.current?.focus(); }
  };

  const chargeToCredit = (customer) => submitSale([{ mode: 'credit', amount: net }], customer);

  const checkout = async () => {
    if (!locationId) return toast.error('Select a location from the top bar first');
    if (!customerOk) return toast.error('Enter the customer’s phone and name');
    if (dayClosed) return toast.error('Billing is closed for the day at this branch');
    if (!session) return toast.error('Open the register before billing');
    if (!cart.length) return toast.error('Cart is empty');
    if (Math.abs(due) > 1) return toast.error(`Collect ${inr(due)} more`);
    await submitSale(payments.length ? payments : [{ mode: 'cash', amount: net }], customerPayload());
  };

  const matches = query.trim()
    ? Object.entries(variantIndex).filter(([, v]) => `${v.name} ${v.code} ${v.packLabel} ${v.productId} ${v.sku || ''} ${v.barcode || ''}`.toLowerCase().includes(query.toLowerCase())).slice(0, 8)
    : [];

  // Nothing to bill with until the register is open: the screen is the opening
  // step instead of a dead till with everything disabled.
  if (!sessionChecked) return <div className="spinner" />;
  if (!session || dayClosed) {
    return (
      <>
        <OpenRegisterCard locationId={locationId} locName={locName} userName={user?.name} dayClosed={dayClosed} lastClose={lastClose}
          onOpened={(sess) => { setSession(sess); announce(); }} />
        {receipt && <Receipt sale={receipt.sale} taxSummary={receipt.taxSummary} billTemplate={billTemplate} onClose={() => setReceipt(null)} />}
      </>
    );
  }

  return (
    <div className="pos-grid">
      <div>
        <form onSubmit={onSubmit} className="pos-scan-row">
          <input ref={scanRef} className="pos-scan" autoFocus placeholder="Scan barcode, or type product name / code / SKU — Enter to add" value={query} onChange={(e) => onQueryChange(e.target.value)} />
          <button type="button" className="btn btn-outline" onClick={() => setShowHeld(true)} disabled={!held.length}
            title={held.length ? `${held.length} bill(s) on hold at this branch` : 'Nothing on hold'}>
            Held{held.length ? ` (${held.length})` : ''}
          </button>
          <button type="button" className="btn btn-outline pos-mute"
            title={muted ? 'Scanner beep off — click to turn on' : 'Scanner beep on — click to mute'}
            onClick={() => { const m = !muted; setMuted(m); setMutedState(m); if (!m) beep(); }}>
            {muted ? '🔇' : '🔊'}
          </button>
        </form>
        {matches.length > 0 && (
          <div className="card mt">
            {matches.map(([vid, v]) => (
              <div key={vid} className="cart-line" style={{ padding: '.5rem .8rem', cursor: 'pointer' }} onClick={() => { addVariant(vid); setQuery(''); }}>
                <div>{v.name} <span className="muted">{v.packLabel}</span>{v.code ? <span className="muted small"> · {v.code}</span> : null}</div>
                <div className="muted">stock {num(stock[vid] || 0)}</div>
                <div>{inr(v.unitPrice)}</div>
                <div className="badge badge-green">Add</div>
              </div>
            ))}
          </div>
        )}

        <div className="card mt">
          <div className={cart.length ? '' : 'card-pad'}>
            {cart.length === 0 ? <div className="empty">Scan or search to add products</div> : (
              <div className="table-wrap cart-table">
                <table className="grid">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th className="right">Qty</th>
                      <th className="right">Discount</th>
                      <th className="right">Amount</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {cart.map((c) => (
                      <tr key={c.key}>
                        <td className="cart-name">{c.name} <span className="muted">{c.packLabel}</span>
                          {stock[c.key] != null && qtyOf(c) > stock[c.key]
                            && <span className="badge badge-red" style={{ marginLeft: 6 }}>only {num(stock[c.key])} in stock</span>}
                          {lineDisc(c) > 0 && <span className="muted small"> · {inr(lineGross(c))} − {inr(lineDisc(c))}</span>}
                        </td>
                        <td className="right">
                          <input className="qty" type="number" min={weighs(c) ? 0.001 : 1} step={weighs(c) ? 'any' : 1}
                            value={c.qty} onChange={(e) => setQty(c.key, weighs(c) ? e.target.value : e.target.value.replace(/[.,].*$/, ''))}
                            onBlur={() => commitQty(c.key)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') e.target.blur();
                              if (!weighs(c) && (e.key === '.' || e.key === ',')) e.preventDefault();
                            }}
                            title={weighs(c) ? `Weight in ${c.unit || 'kg'}` : 'Whole packs only'} />
                        </td>
                        <td className="right">
                          <span className="disc-box" title="Discount on this line">
                            <input type="number" min="0" step="0.01" placeholder="0" value={c.disc}
                              onChange={(e) => setLineDisc(c.key, { disc: e.target.value })} />
                            <button type="button" className="disc-mode"
                              onClick={() => setLineDisc(c.key, { discMode: c.discMode === 'pct' ? 'amt' : 'pct' })}>
                              {c.discMode === 'pct' ? '%' : '₹'}
                            </button>
                          </span>
                        </td>
                        <td className="right amt">{inr(lineNet(c))}</td>
                        <td className="right">
                          <button className="btn btn-danger btn-sm" onClick={() => removeLine(c.key)}>✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="pos-cart">
        <div className="card card-pad">
          <div className="bill-row"><span>Items</span><span>{num(cart.reduce((s, c) => s + qtyOf(c), 0))}</span></div>
          <div className="bill-row"><span>Subtotal (incl GST)</span><span>{inr(gross)}</span></div>
          {lineDiscTotal > 0 && (
            <div className="bill-row muted"><span>Line discounts</span><span>−{inr(lineDiscTotal)}</span></div>
          )}
          <div className="bill-row">
            <span>Discount on bill</span>
            <span className="flex gap" style={{ alignItems: 'center' }}>
              {billDisc > 0 && discountMode === 'pct' && <span className="muted small">−{inr(billDisc)}</span>}
              <span className="disc-box">
                <input type="number" min="0" step="0.01" placeholder="0" value={discount} onChange={(e) => setDiscount(e.target.value)} />
                <button type="button" className="disc-mode" onClick={() => setDiscountMode(discountMode === 'pct' ? 'amt' : 'pct')}>
                  {discountMode === 'pct' ? '%' : '₹'}
                </button>
              </span>
            </span>
          </div>
          {discTotal > 0 && <div className="bill-row"><span>Total discount</span><span className="neg">−{inr(discTotal)}</span></div>}
          <div className="bill-row total"><span>To pay</span><span>{inr(net)}</span></div>

          <div className="mt">
            <div className="pay-split">
              {['cash', 'upi', 'card'].map((m) => {
                const used = payments.some((p) => p.mode === m);
                return <button key={m} className="pill-btn" disabled={used || payments.length >= 2} onClick={() => addPay(m)}>{m === 'upi' ? 'UPI' : m[0].toUpperCase() + m.slice(1)}</button>;
              })}
              <span className="pay-split-sep" />
              <button className="pill-btn" onClick={() => setPayLater(true)} disabled={busy || !cart.length}>Pay later</button>
              <button className="pill-btn" onClick={() => setHolding(true)} disabled={busy || !cart.length}>Hold</button>
            </div>
            {payments.length >= 2 && <p className="muted small" style={{ margin: '.2rem 0' }}>Up to two payment methods. Edit one — the other adjusts to the balance.</p>}
            {payments.map((p, i) => (
              <div key={i} className="bill-row">
                <span>{p.mode.toUpperCase()}</span>
                <span className="flex gap">
                  <input type="number" style={{ width: 100, textAlign: 'right' }} value={p.amount} onChange={(e) => setPayAmount(i, e.target.value)} />
                  <button className="btn btn-danger btn-sm" onClick={() => rmPay(i)}>✕</button>
                </span>
              </div>
            ))}
            <div className="bill-row" style={{ color: Math.abs(due) > 1 ? 'var(--danger)' : 'var(--ok)' }}><span>Balance</span><span>{inr(due)}</span></div>
          </div>

          {short.length > 0 && (
            <p className="form-error" style={{ margin: '.5rem 0 0' }}>
              {short.length === 1
                ? `${short[0].name} ${short[0].packLabel}: only ${num(short[0].have)} on the books, billing ${num(short[0].qty)}.`
                : `${short.length} items are being billed beyond what the books hold.`}
              {' '}The sale will go through and the stock will show short.
            </p>
          )}
          <div style={{ margin: '.7rem 0 .2rem' }}>
            <CustomerFields customer={customer} setCustomer={setCustomer} known={knownCustomer} setKnown={setKnownCustomer} required />
            <ShipTo customerId={knownCustomer && knownCustomer.id} addresses={addresses} shipToId={shipToId} setShipToId={setShipToId}
              onAdded={(a) => { setAddresses((prev) => [...prev, a]); setShipToId(String(a.id)); }} />
          </div>
          <button className="btn btn-gold btn-block mt" onClick={checkout} disabled={busy || !cart.length || !customerOk}>{busy ? 'Saving…' : `Charge ${inr(net)}`}</button>
          {cart.length > 0 && !customerOk && (
            <p className="muted small" style={{ textAlign: 'center', margin: '.35rem 0 0' }}>Enter the customer’s phone and name to charge this bill.</p>
          )}

        </div>
      </div>

      <RegisterBar session={session} onCloseRegister={() => setClosing(true)} />
      {holding && <HoldModal total={net} items={cart.length} onHold={holdBill} onClose={() => setHolding(false)} />}
      {showHeld && <HeldModal held={held} onResume={resumeHeld} onDiscard={discardHeld} onClose={() => setShowHeld(false)} />}
      {payLater && <PayLaterModal total={net} customer={customer} onConfirm={chargeToCredit} onClose={() => setPayLater(false)} />}
      {receipt && <Receipt sale={receipt.sale} taxSummary={receipt.taxSummary} billTemplate={billTemplate} onClose={() => setReceipt(null)} />}
      {closing && session && <CloseRegisterModal session={session} onClosed={() => { setSession(null); announce(); }} onClose={() => setClosing(false)} />}
    </div>
  );
};

export default Billing;
